import type {
  DataProvider,
  DashboardData,
  BookingsData,
  AnalyticsData,
  PayoutData
} from "./types";
import type { Studio, Room, Profile } from "@/types/database";
import type { BookingWithDetails } from "@/components/bookings/bookings-data-table";
import { generateMockData } from "@/lib/analytics-mock-data";
import { 
  DEMO_USER, 
  DEMO_STUDIO, 
  DEMO_ROOMS, 
  generateDemoBookings,
  generateDemoPayouts 
} from "./demo-data-generator";
import { isToday } from "date-fns";

export class DemoDataProvider implements DataProvider {
  private bookings: BookingWithDetails[] = [];
  private payouts: PayoutData[] = [];
  private studio: Studio = DEMO_STUDIO;
  private rooms: Room[] = DEMO_ROOMS;
  private user: Profile = DEMO_USER;

  constructor() {
    // Initialize demo data
    this.bookings = generateDemoBookings();
    this.payouts = generateDemoPayouts();
  }

  async getDashboardData(): Promise<DashboardData> {
    // Simulate loading delay
    await this.simulateDelay();
    
    const pendingBookings = this.bookings
      .filter(b => b.status === "pending")
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    
    const todayBookings = this.bookings
      .filter(b => {
        const startTime = new Date(b.start_time);
        return b.status === "approved" && isToday(startTime);
      })
      .sort((a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime());
    
    return {
      studio: this.studio,
      pendingBookings,
      todayBookings,
    };
  }

  async getBookingsData(): Promise<BookingsData> {
    await this.simulateDelay();
    
    return {
      studios: [this.studio],
      rooms: this.rooms,
      bookings: this.bookings.sort((a, b) => 
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      ),
    };
  }

  async getBookingDetail(bookingId: string): Promise<BookingWithDetails | null> {
    await this.simulateDelay();
    return this.bookings.find(b => b.id === bookingId) || null;
  }

  async approveBooking(bookingId: string): Promise<boolean> {
    await this.simulateDelay();
    
    const booking = this.bookings.find(b => b.id === bookingId);
    if (booking && booking.status === "pending") {
      booking.status = "approved";
      return true;
    }
    return false;
  }

  async denyBooking(bookingId: string): Promise<boolean> {
    await this.simulateDelay();
    
    const booking = this.bookings.find(b => b.id === bookingId);
    if (booking && booking.status === "pending") {
      booking.status = "denied";
      return true;
    }
    return false;
  }

  async getAnalyticsData(): Promise<AnalyticsData> {
    const dateRange = 90;
    await this.simulateDelay();
    
    // Generate analytics snapshots using existing mock data generator
    const { snapshots } = generateMockData(dateRange, this.rooms);
    
    return {
      studio: this.studio,
      rooms: this.rooms,
      snapshots,
    };
  }

  async getStudio(): Promise<Studio | null> {
    await this.simulateDelay();
    return this.studio;
  }

  async getStudios(): Promise<Studio[]> {
    await this.simulateDelay();
    return [this.studio];
  }

  async getRooms(studioId: string): Promise<Room[]> {
    await this.simulateDelay();
    return this.rooms.filter(r => r.studio_id === studioId);
  }

  async getRoom(roomId: string): Promise<Room | null> {
    await this.simulateDelay();
    return this.rooms.find(r => r.id === roomId) || null;
  }

  async createRoom(room: Partial<Room>, studioId: string): Promise<Room> {
    await this.simulateDelay();
    
    const newRoom: Room = {
      id: `demo-room-${Date.now()}`,
      studio_id: studioId,
      name: room.name || "New Room",
      description: room.description,
      capacity: room.capacity,
      equipment: room.equipment,
      photos: room.photos,
      is_active: room.is_active ?? true,
      dimensions: room.dimensions,
      created_at: new Date().toISOString(),
      room_rates: [],
    };
    
    this.rooms.push(newRoom);
    return newRoom;
  }

  async updateRoom(roomId: string, updates: Partial<Room>): Promise<Room> {
    await this.simulateDelay();
    
    const roomIndex = this.rooms.findIndex(r => r.id === roomId);
    if (roomIndex === -1) {
      throw new Error("Room not found");
    }
    
    this.rooms[roomIndex] = {
      ...this.rooms[roomIndex],
      ...updates,
      updated_at: new Date().toISOString(),
    };
    
    return this.rooms[roomIndex];
  }

  async getPayouts(): Promise<PayoutData[]> {
    await this.simulateDelay();
    return this.payouts;
  }

  async getPayoutDetail(payoutId: string): Promise<PayoutData | null> {
    await this.simulateDelay();
    return this.payouts.find(p => p.id === payoutId) || null;
  }

  async updateStudioSettings(studioId: string, settings: Partial<Studio>): Promise<Studio> {
    await this.simulateDelay();
    
    if (this.studio.id === studioId) {
      this.studio = {
        ...this.studio,
        ...settings,
        updated_at: new Date().toISOString(),
      };
    }
    
    return this.studio;
  }

  async getCurrentUser(): Promise<Profile | null> {
    await this.simulateDelay();
    return this.user;
  }

  async modifyBooking(
    bookingId: string,
    localSlots: Array<{ date: string; time: string }>
  ): Promise<BookingWithDetails | null> {
    await this.simulateDelay();

    const bookingIndex = this.bookings.findIndex(b => b.id === bookingId);
    if (bookingIndex === -1) {
      throw new Error("Rental not found");
    }

    // Convert local slots to UTC-like format for demo storage
    // In real app, server does this with proper timezone handling
    const slots = localSlots.map(slot => {
      const startTime = new Date(`${slot.date}T${slot.time}:00`);
      const endTime = new Date(startTime.getTime() + 30 * 60 * 1000);
      return {
        start_time: startTime.toISOString(),
        end_time: endTime.toISOString(),
      };
    });

    // Sort slots and get first/last for booking times
    const sortedSlots = [...slots].sort(
      (a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime()
    );
    const firstSlot = sortedSlots[0];
    const lastSlot = sortedSlots[sortedSlots.length - 1];

    // Update the booking with new slots
    this.bookings[bookingIndex] = {
      ...this.bookings[bookingIndex],
      start_time: firstSlot.start_time,
      end_time: lastSlot.end_time,
      booking_slots: slots.map(slot => ({
        start_time: slot.start_time,
        end_time: slot.end_time,
      })),
      updated_at: new Date().toISOString(),
    };

    return this.bookings[bookingIndex] as BookingWithDetails;
  }

  private async simulateDelay() {
    // Simulate network delay for more realistic demo experience
    await new Promise(resolve => setTimeout(resolve, 300 + Math.random() * 200));
  }
}